import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { User } from 'src/app/models/User';

import { DomainAddonPrice } from './../../../models/DomainAddonPrice';
import { ConfirmationService } from './../../../shared/components/confirmation-modal/confirmation.service';
import { ToastService } from './../../../shared/components/toast/toast.service';
import { AuthService } from './../../../shared/services/auth.service';
import { DomainAddonPriceService } from './../../../shared/services/domain-addon-price.service';
import { ADMINISTRATOR, CUSTOMER_SUCCESS } from './../../../shared/services/user.service';

@Component({
  selector: 'app-dominio-precos',
  templateUrl: './dominio-precos.component.html',
  styleUrls: ['./dominio-precos.component.scss'],
})
export class DominioPrecosComponent implements OnInit {
  user: User;
  loading = true;

  /**
   * Botão em andamento: `save-<id>`, `remove-<id>` ou `add`. Ele gira e
   * `working` trava os outros — a tela saiu do overlay global (CU-86akkubzz).
   */
  busyKey: string | null = null;

  get working(): boolean {
    return this.busyKey !== null;
  }

  basePrices: DomainAddonPrice[] = [];
  tlds: DomainAddonPrice[] = [];

  newTld = { tld: '', price: null as number | null, cost: null as number | null };

  private baseLabels: { [type: string]: string } = {
    addon_byod: 'Assinatura do add-on — BYOD (12 meses)',
    addon_purchase: 'Assinatura do add-on — Compra (12 meses)',
    transfer: 'Transferência de titularidade (única)',
  };

  constructor(
    private router: Router,
    private authService: AuthService,
    private toastService: ToastService,
    private confirmationService: ConfirmationService,
    private priceService: DomainAddonPriceService
  ) { }

  ngOnInit(): void {
    this.user = this.authService.getUser();

    if (![ADMINISTRATOR, CUSTOMER_SUCCESS].includes(this.user?.profile_id)) {
      this.router.navigate(['/dominio-proprio']);
      return;
    }

    this.load();
  }

  load(): void {
    this.loading = true;

    this.priceService.list().subscribe({
      next: prices => {
        this.basePrices = prices.filter(p => p.type !== 'tld');
        this.tlds = prices.filter(p => p.type === 'tld');
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toastService.error('Erro ao carregar os preços.');
      },
    });
  }

  label(price: DomainAddonPrice): string {
    return this.baseLabels[price.type] || price.type;
  }

  margin(price: DomainAddonPrice): number {
    return (price.price || 0) - (price.cost || 0);
  }

  save(price: DomainAddonPrice): void {
    if (price.price === null || price.price < 0) {
      this.toastService.warning('Informe um preço de venda válido.');
      return;
    }

    this.busyKey = `save-${price.id}`;

    this.priceService.update(price.id, {
      price: +price.price,
      cost: price.cost !== null && price.cost !== undefined ? +price.cost : null,
      active: !!price.active,
    }).subscribe({
      next: updated => {
        this.busyKey = null;
        Object.assign(price, updated);
        this.toastService.success('Preço atualizado!');
      },
      error: res => {
        this.busyKey = null;

        if (res.status !== 422) {
          this.toastService.error('Erro ao salvar o preço.');
        }
      },
    });
  }

  addTld(): void {
    const tld = (this.newTld.tld || '').trim().toLowerCase().replace(/^\./, '');

    if (!tld || this.newTld.price === null) {
      this.toastService.warning('Informe a extensão (TLD) e o preço de venda.');
      return;
    }

    this.busyKey = 'add';

    this.priceService.create({
      type: 'tld',
      tld,
      price: +this.newTld.price,
      cost: this.newTld.cost !== null ? +this.newTld.cost : null,
      active: true,
    }).subscribe({
      next: created => {
        this.busyKey = null;
        this.tlds = [...this.tlds, created].sort((a, b) => (a.tld || '').localeCompare(b.tld || ''));
        this.newTld = { tld: '', price: null, cost: null };
        this.toastService.success(`TLD .${created.tld} cadastrado!`);
      },
      error: res => {
        this.busyKey = null;

        if (res.status !== 422) {
          this.toastService.error('Erro ao cadastrar o TLD.');
        }
      },
    });
  }

  removeTld(price: DomainAddonPrice): void {
    this.confirmationService.show(
      `Excluir o preço do TLD <b>.${price.tld}</b>? Domínios novos dessa extensão ficarão indisponíveis no fluxo Compra.`,
      () => {
        this.busyKey = `remove-${price.id}`;

        this.priceService.delete(price.id).subscribe({
          next: () => {
            this.busyKey = null;
            this.tlds = this.tlds.filter(t => t.id !== price.id);
            this.toastService.success('TLD excluído.');
          },
          error: res => {
            this.busyKey = null;

            if (res.status !== 422) {
              this.toastService.error('Erro ao excluir o TLD.');
            }
          },
        });
      }
    );
  }
}
